import { useEffect, useMemo, useState } from 'react';
import { useFetcher } from 'react-router';
import type { GetMatchedSubmitResponse } from '~/routes/remix-api.get-matched.submit';

export interface MatchAnswersForSubmit {
    /** 'professional' | 'project' | 'product' — null defaults to 'professional' server-side. */
    enquiryType: string | null;
    userType: string | null;
    region: string | null;
    projectType: string | null;
    projectScope: string | null;
    style: string | null;
    budget: string | null;
    description: string;
    professionalType: string | null;
    startDate: string | null;
    endDate: string | null;
    email: string;
    attachments: File[];
    buildHubID: number | null;
}

export type SubmissionStatus = 'idle' | 'submitting' | 'success' | 'error';

interface UseGetMatchedSubmissionResult {
    status: SubmissionStatus;
    errorMessage: string | null;
    submit: (answers: MatchAnswersForSubmit) => void;
    reset: () => void;
}

export const useGetMatchedSubmission = (): UseGetMatchedSubmissionResult => {
    const fetcher = useFetcher<GetMatchedSubmitResponse>();
    const [hasSubmitted, setHasSubmitted] = useState(false);
    // `fetcher.data` lingers between submissions — Remix has no built-in
    // reset. This flag only flips true once we observe the in-flight state
    // for the *current* submission. Without it, a fresh submit() could read
    // the previous round's `data` and report a stale 'success' / 'error'.
    const [hasSeenSubmittingForCurrent, setHasSeenSubmittingForCurrent] =
        useState(false);

    useEffect(() => {
        if (
            !hasSeenSubmittingForCurrent &&
            (fetcher.state === 'submitting' || fetcher.state === 'loading')
        ) {
            setHasSeenSubmittingForCurrent(true);
        }
    }, [fetcher.state, hasSeenSubmittingForCurrent]);

    const status: SubmissionStatus = useMemo(() => {
        if (!hasSubmitted) return 'idle';
        if (fetcher.state !== 'idle') return 'submitting';
        if (!hasSeenSubmittingForCurrent) return 'submitting';
        if (!fetcher.data) return 'submitting';
        return fetcher.data.success ? 'success' : 'error';
    }, [
        hasSubmitted,
        fetcher.state,
        fetcher.data,
        hasSeenSubmittingForCurrent,
    ]);

    const errorMessage =
        hasSubmitted &&
        hasSeenSubmittingForCurrent &&
        fetcher.data &&
        !fetcher.data.success
            ? fetcher.data.errorMessage
            : null;

    const submit = (answers: MatchAnswersForSubmit) => {
        const formData = new FormData();
        formData.append('enquiryType', answers.enquiryType ?? '');
        formData.append('userType', answers.userType ?? '');
        formData.append('region', answers.region ?? '');
        formData.append('projectType', answers.projectType ?? '');
        formData.append('projectScope', answers.projectScope ?? '');
        formData.append('style', answers.style ?? '');
        formData.append('budget', answers.budget ?? '');
        formData.append('description', answers.description);
        formData.append('professionalType', answers.professionalType ?? '');
        formData.append('startDate', answers.startDate ?? '');
        formData.append('endDate', answers.endDate ?? '');
        formData.append('email', answers.email);
        if (answers.buildHubID != null) {
            formData.append('buildHubID', String(answers.buildHubID));
        }
        formData.append(
            'submittedFromURL',
            typeof window !== 'undefined' ? window.location.href : '',
        );
        answers.attachments.forEach((file, index) => {
            formData.append(`Attachments[${index}]`, file, file.name);
        });

        setHasSeenSubmittingForCurrent(false);
        setHasSubmitted(true);
        fetcher.submit(formData, {
            method: 'POST',
            action: '/remix-api/get-matched/submit',
            encType: 'multipart/form-data',
        });
    };

    const reset = () => {
        setHasSubmitted(false);
        setHasSeenSubmittingForCurrent(false);
    };

    return { status, errorMessage, submit, reset };
};
